import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { arrayOf, bool, func, number, oneOfType, string } from 'prop-types';
import classnames from 'classnames';
import './CompensationSummaryHeader.scss';
import { getFormData } from '../../../lib/helpers';
import { shortDateString } from 'ps-components/lib/formatters';
import Button from 'ps-components/Button/Button';
import CheckboxInput from 'ps-components/Input/CheckboxInput';
import Input from 'ps-components/Input/Input';
import Dropdown from 'ps-components/Dropdown/Dropdown';
import addAlert from 'ps-components/AlertToast/addAlert';
import { PRICING_TYPE } from '../../SkillGeoDifferential/SkillGeoDifferentialReducer';
import { useCSR, useOrgJobs, useSession } from '../../../hooks/redux';
import { isAdHocJobId } from '../../MarketPricing/helper';

const MAX_LENGTH = '100';
const MAX_SELECTED_JOBS_DOWNLOAD = 50;
function CompensationSummaryHeader(props) {
  const [isIframed, setIframed] = useState(false);
  const [showSettings, setSettings] = useState(false);
  const [tempCoverPage, setTempCoverPage] = useState('');
  const [tempFooter, setTempFooter] = useState('');
  const [tempHeader, setTempHeader] = useState('');
  const [tempShowCompanyLogo, setTempShowCompanyLogo] = useState('');
  const [tempShowPayScaleLogo, setTempShowPayScaleLogo] = useState('');
  const [tempReportName, setTempReportName] = useState('Position Compensation Summary of');
  const [popoverLeftPosition, setPopoverLeftPosition] = useState(0);
  const popoverRef = useRef(null);
  const pdfSettingsButtonRef = useRef(null);

  const CSRData = useCSR();
  const orgJobsData = useOrgJobs();
  const sessionData = useSession();

  const positionTitle = orgJobsData.selectedJob.title;
  const jobCode = orgJobsData.selectedJob.code;
  const jobCodeKey = orgJobsData.selectedJob.codeKey;
  const orgJobId = orgJobsData.selectedJob.id;
  const csrfPair = sessionData.csrfPair;
  const launchDarklyFeatureFlags = props.launchDarklyFeatureFlags;

  const {
    customFooter,
    customHeader,
    isConfigVisible,
    isDownloading,
    reportName,
    showCoverPage,
    showCompanyLogo,
    showPayScaleLogo,
    showInternalData,
    showPricingDetails,
    showSurveyJobDescription,
    showEmployeeData,
    showLastModified,
    showMarketRange,
    showMatchDetails,
    showOverallComposites
  } = CSRData;

  const { jobPricingGrid } = props;
  const { employeeData, internalData } = CSRData.data;

  useEffect(() => {
    const iframe = document.querySelector('iframe.frame-iframe');
    if (iframe) setIframed(true);
    window.addEventListener('resize', adjustPopoverLeftPosition());

    return () => {
      window.removeEventListener('resize', adjustPopoverLeftPosition());
    };
  }, []);

  useEffect(() => {
    const iframe = document.querySelector('iframe.frame-iframe');
    if (iframe && !isIframed) {
      setIframed(true);
    }

    setTempFooter(customFooter || '');
    setTempHeader(customHeader || '');
    setTempReportName(reportName || 'Position Compensation Summary of');
  }, [customFooter, customHeader, reportName]);

  const orgJobs = useSelector(state => state.orgJobs);
  const selectedViewPk = useSelector(
    state => state.marketPricing.JobPricingReducer.MainJobPricingReducer.selectedViewPk
  );

  function downloadToPdf(isBulkDownload, targetFileType) {
    props.downloadHandler(isBulkDownload, targetFileType);
  }

  function handleDownloadToExcel() {
    const {
      downloadToExcel,
      benchmarkYear,
      selectedEmployeesViewPk,
      selectedJobDetailsViewPk,
      pricingType,
      isRestrictedUser,
      lastModifiedDate,
      lastModifiedName,
      showJobCodeKey
    } = props;
    const iframe = document.querySelector('iframe.frame-iframe');
    if (iframe && launchDarklyFeatureFlags['show-old-csr-download-to-excel']) {
      let payDivisor = 1, // Pay divisor defaulted to 1 for CSR opening on non-MPAJ pages
        activeSurveyYear = benchmarkYear; // Benchmark year defaulted to aging.benchmarkYear
      if (iframe?.contentWindow?.window?.app?.controllers.keys.includes('pricingTabLauncher')) {
        let activeTab = iframe?.contentWindow?.window?.app.getController('pricingTabLauncher').getActiveJobTab();
        payDivisor = activeTab ? activeTab.job_data.pay_divisor : payDivisor;
        activeSurveyYear = activeTab
          ? Number.parseInt(activeTab.query('benchmarkSummaryCombo')[0].getValue())
          : activeSurveyYear;
      }
      const data = {
        download_type: 'xls',
        org_job_id: orgJobId,
        summary_type: 'one_page_summary',
        include_ees: !isRestrictedUser && showEmployeeData,
        include_graph: !isRestrictedUser && showMarketRange,
        include_matches: showMatchDetails,
        active_survey_year: activeSurveyYear,
        active_pay_divisor: payDivisor,
        csrf_token: csrfPair.csrf_token,
        csrf_timestamp: csrfPair.csrf_timestamp,
        selected_employee_view_pk: selectedEmployeesViewPk,
        selected_job_details_view_pk: selectedJobDetailsViewPk
      };

      const formattedData = getFormData(data);
      downloadToExcel('OLD', formattedData);
    } else {
      const options = {
        showInternalData,
        showPricingDetails,
        showSurveyJobDescription,
        showEmployeeData: !isRestrictedUser && showEmployeeData,
        showLastModified,
        showMatchDetails,
        showOverallComposites
      };

      const { selectedJob } = orgJobs;
      const { code, codeKey, title } = selectedJob;
      const agingDate = dataAgedToDate;
      const jobDetails = {
        pricingType,
        code,
        codeKey,
        title,
        agingDate,
        benchmarkYear,
        lastModifiedName,
        lastModifiedDate,
        showJobCodeKey,
        selectedViewPk
      };
      const requestData = { internalData, jobPricingGrid, employeeData, jobDetails, options };

      downloadToExcel('NEW', requestData);
    }
  }

  function handleDropdownClick(e) {
    if (e === 'As Spreadsheet') handleDownloadToExcel();
    else if (e === 'Current Job as PDF' || e === 'As PDF') downloadToPdf(false);
    else if (e.includes('All Selected Jobs')) {
      const targetFileType = e.includes('single PDF') ? 'PDF' : 'ZIP';
      if (props.selectedJobIDs.length > MAX_SELECTED_JOBS_DOWNLOAD) {
        addAlert(`Only up to ${MAX_SELECTED_JOBS_DOWNLOAD} jobs can be downloaded`, 'warning', 'manual');
      } else {
        addAlert('Downloading reports will take a minute or two', 'info', 'manual');
        downloadToPdf(true, targetFileType);
      }
    }
  }

  function closeModal() {
    const { clearSelectedJobs, hideModal, setSelectedBenchmarkYear } = props;
    hideModal();
    setIframed(false);
    clearSelectedJobs();
    setSelectedBenchmarkYear('');
  }

  function getDropdownItems() {
    const { selectedJobIDs, pricingType, benchmarkYear } = props;

    let hasJobData = false;
    const multipleJobs = selectedJobIDs.length > 1;

    if (isIframed) {
      const iframe = document.querySelector('#acs-iframe');
      if (iframe?.contentWindow?.window?.app?.controllers?.map?.pricingTabLauncher) {
        const tab = iframe.contentWindow.window.app.getController('pricingTabLauncher').getActiveJobTab();
        if (tab && tab.job_data) hasJobData = true;
      }
    }

    if (orgJobId && benchmarkYear) {
      hasJobData = true;
    }

    const dropdownItems = [
      <div key="single-pdf" className="comp-sum-header__config-action-pdf">
        <i className="icon-file-pdf icon-red" />
        {multipleJobs ? 'Current Job as PDF' : 'As PDF'}
      </div>
    ];

    if (
      hasJobData &&
      !multipleJobs &&
      (!document.querySelector('iframe.frame-iframe') ||
        (document.querySelector('iframe.frame-iframe') && pricingType !== PRICING_TYPE.SLOTTED))
    ) {
      dropdownItems.push(
        <div key="single-excel" className="comp-sum-header__config-action-excel">
          <i className="icon-file-excel icon-green" />
          As Spreadsheet
        </div>
      );
    }

    if (selectedJobIDs.length > 1) {
      const classNames = classnames('comp-sum-header__config-action-all-jobs', {
        disabled: selectedJobIDs.length > MAX_SELECTED_JOBS_DOWNLOAD
      });
      dropdownItems.push(
        <div key="bulk-pdf" className={classNames}>
          <i className="icon-docs icon-blue" />
          All Selected Jobs (zip file)
        </div>
      );
      dropdownItems.push(
        <div key="bulk-pdf-concat" className={classNames}>
          <i className="icon-file-pdf icon-red" />
          All Selected Jobs (single PDF)
        </div>
      );
    }

    return dropdownItems;
  }

  function adjustPopoverLeftPosition() {
    const pdfSettingsButton = pdfSettingsButtonRef.current;
    const popover = popoverRef.current;

    if (pdfSettingsButton && popover) {
      const pdfSettingsButtonLeftPosition = pdfSettingsButton.getBoundingClientRect().left;
      const centerWidthOfPdfButton = pdfSettingsButton.offsetWidth / 2;
      const popoverWidth = popover.offsetWidth;
      setPopoverLeftPosition(pdfSettingsButtonLeftPosition + centerWidthOfPdfButton - popoverWidth);
    }
  }

  function saveCoverPageSetting() {
    const newState = typeof tempCoverPage === 'boolean' ? tempCoverPage : showCoverPage;

    setTempCoverPage(!newState);
  }

  function saveShowCompanyLogo() {
    const newState = typeof tempShowCompanyLogo === 'boolean' ? tempShowCompanyLogo : showCompanyLogo;

    setTempShowCompanyLogo(!newState);
  }

  function saveShowPayScaleLogo() {
    const newState = typeof tempShowPayScaleLogo === 'boolean' ? tempShowPayScaleLogo : showPayScaleLogo;

    setTempShowPayScaleLogo(!newState);
  }

  function storeConfigSettings() {
    const { saveConfigSettings } = props;

    saveConfigSettings({
      showCoverPage: typeof tempCoverPage === 'boolean' ? tempCoverPage : showCoverPage,
      customHeader: tempHeader,
      customFooter: tempFooter,
      reportName: tempReportName,
      showCompanyLogo: typeof tempShowCompanyLogo === 'boolean' ? tempShowCompanyLogo : showCompanyLogo,
      showPayScaleLogo: typeof tempShowPayScaleLogo === 'boolean' ? tempShowPayScaleLogo : showPayScaleLogo
    });

    setSettings(false);
    setTempCoverPage('');
    setTempFooter('');
    setTempHeader('');
    setTempShowCompanyLogo('');
    setTempShowPayScaleLogo('');
  }

  function setTempState(e) {
    if (e.target.id === 'tempHeader') {
      setTempHeader(e.target.value);
    } else if (e.target.id === 'tempFooter') {
      setTempFooter(e.target.value);
    } else if (e.target.id === 'tempReportName') {
      setTempReportName(e.target.value);
    }
  }

  function getHeaderLength() {
    return tempHeader ? MAX_LENGTH - tempHeader.length : MAX_LENGTH;
  }

  function getFooterLength() {
    return tempFooter ? MAX_LENGTH - tempFooter.length : MAX_LENGTH;
  }

  function getReportNameLength() {
    return tempReportName ? MAX_LENGTH - tempReportName.length : MAX_LENGTH;
  }

  function getPositionTitleHeader() {
    const { useJobCodeKey } = props;
    if (isAdHocJobId(orgJobId)) {
      return 'Ad Hoc pricing';
    }

    let title = jobCode + ' ';
    if (useJobCodeKey && jobCodeKey) {
      title += jobCodeKey + ' ';
    }

    title += positionTitle;
    return title;
  }

  function renderPopover() {
    return (
      <div className="popover" ref={popoverRef} style={{ left: popoverLeftPosition + 'px' }}>
        <div className="popover__row">
          <div className="pxl-header-xxsmall row__header">Header</div>
          <Input
            id="tempHeader"
            onChange={e => setTempState(e)}
            maxLength={MAX_LENGTH}
            type="text"
            value={tempHeader}
          />
          <div className="popover__row-count">{getHeaderLength()}</div>
        </div>
        <div className="popover__row">
          <div className="pxl-header-xxsmall row__header">Report Name</div>
          <Input
            id="tempReportName"
            onChange={e => setTempState(e)}
            maxLength={MAX_LENGTH}
            type="text"
            value={tempReportName}
          />
          <div className="popover__row-count">{getReportNameLength()}</div>
        </div>
        <div className="popover__row">
          <div className="pxl-header-xxsmall row__header">Footer</div>
          <Input
            id="tempFooter"
            onChange={e => setTempState(e)}
            maxLength={MAX_LENGTH}
            type="text"
            value={tempFooter}
          />
          <div className="popover__row-count">{getFooterLength()}</div>
        </div>
        <div className="popover__row">
          <div className="setting-toggle-action pxl-header-xxsmall">
            <CheckboxInput
              checked={typeof tempCoverPage === 'boolean' ? tempCoverPage : showCoverPage}
              className="setting-toggle-checkbox"
              onClick={saveCoverPageSetting}
            />
            Include Cover Page
          </div>
        </div>
        <div className="popover__row">
          <div className="setting-toggle-action pxl-header-xxsmall csr-include-company-logo">
            <CheckboxInput
              checked={typeof tempShowCompanyLogo === 'boolean' ? tempShowCompanyLogo : showCompanyLogo}
              className="setting-toggle-checkbox"
              onClick={saveShowCompanyLogo}
            />
            Include Company logo in the header
          </div>
        </div>
        <div className="popover__row">
          <div className="setting-toggle-action pxl-header-xxsmall csr-include-payscale-logo">
            <CheckboxInput
              checked={typeof tempShowPayScaleLogo === 'boolean' ? tempShowPayScaleLogo : showPayScaleLogo}
              className="setting-toggle-checkbox"
              onClick={saveShowPayScaleLogo}
            />
            Include PayScale logo in the footer
          </div>
        </div>
        <div className="popover__footer">
          <a onClick={hideSettings}>Cancel</a>
          <Button className="pxl-btn pxl-btn-primary pxl-margin-small" onClick={() => storeConfigSettings()}>
            Save
          </Button>
        </div>
      </div>
    );
  }

  function toggleConfigModal() {
    const { toggleConfig } = props;
    toggleConfig();
    setSettings(false);
    setTempCoverPage(showCoverPage);
    setTempFooter(customFooter);
    setTempHeader(customHeader);
    setTempReportName(reportName);
  }

  function displaySettings() {
    setSettings(true);
    adjustPopoverLeftPosition();
  }

  function hideSettings() {
    setSettings(false);
    setTempCoverPage(showCoverPage);
    setTempFooter(customFooter);
    setTempHeader(customHeader);
    setTempReportName(reportName || 'Position Compensation Summary of');
  }

  const {
    dataAgedToDate,
    goToNextJob,
    goToPreviousJob,
    isPageLoading,
    lastModifiedDate,
    lastModifiedName,
    selectedJobIDs,
    showModifedHeader
  } = props;

  if (isPageLoading) {
    return (
      <div className="comp-sum-header">
        <div className="comp-sum-header__row comp-sum__loading">
          <span className="comp-sum-modal__close" onClick={closeModal}>
            <i className="icon-cancel" />
          </span>
        </div>
      </div>
    );
  }

  const dropdownItems = getDropdownItems();
  const formattedDate = lastModifiedDate ? shortDateString(new Date(lastModifiedDate.replace(/-/g, '/'))) : '';
  const hasAllData = lastModifiedName && formattedDate;
  const hasNoData = !lastModifiedDate && !formattedDate;
  const auditMessage =
    (showLastModified && hasNoData) || !showLastModified
      ? ''
      : showLastModified && hasAllData
      ? `Last modified by ${lastModifiedName} on ${formattedDate}`
      : `Last modified on ${formattedDate}`;
  const isBulkMode = selectedJobIDs.length > 1;
  const headerNote = reportName ? reportName : 'Position Compensation Summary of';
  const headerNoteClass = classnames({
    'pxl-text-small': true,
    'comp-sum-header__subtitle': true,
    'comp-sum-header__subtitle-bold': isBulkMode
  });

  if (showModifedHeader) {
    return (
      <div className="comp-sum-header-modified">
        <div className="comp-sum-header__row">
          <div className="comp-sum-header__row">
            <h2 className="pxl-header-xsmall comp-sum-header__title">{getPositionTitleHeader()}</h2>
            <div className="single__row-header">
              <div className="comp-sum-header__actions">
                {!isConfigVisible ? (
                  <React.Fragment>
                    <div
                      className="comp-sum-header__config-action"
                      onClick={displaySettings}
                      ref={pdfSettingsButtonRef}
                    >
                      <i className="icon-cog comp-sum-header__config-action-icon" />
                      PDF Settings
                    </div>
                    <div className="comp-sum-header__config-action" onClick={toggleConfigModal}>
                      <i className="icon-pencil comp-sum-header__config-action-icon" />
                      Configure Report
                    </div>
                  </React.Fragment>
                ) : (
                  <a className="comp-sum-header__config-action" onClick={toggleConfigModal}>
                    Done
                  </a>
                )}
                <Dropdown
                  className="pxl-btn pxl-btn-primary pxl-btn-sm comp-sum-header__config-action-dropdown"
                  dropdownClass="comp-sum-header__config-action-dropdown-items"
                  itemOnClick={handleDropdownClick}
                  dropdownItems={dropdownItems}
                  icon={!isDownloading ? 'icon-download' : 'icon-spin icon-animate-spin'}
                  text={!isDownloading ? 'Download' : 'Downloading'}
                />
              </div>
              <span className="comp-sum-modal__close" onClick={closeModal}>
                <i className="icon-cancel" />
              </span>
            </div>
          </div>
        </div>
        {showSettings && renderPopover()}
      </div>
    );
  }

  return (
    <div className="comp-sum-header">
      <div className="comp-sum-header__row">
        <h4 className={headerNoteClass}>{headerNote}</h4>
        <div className="comp-sum-header__modal-actions">
          {isBulkMode && (
            <div className="comp-sum-header__page-navigation">
              <a className="comp-sum-header__page-navigation-link" onClick={goToPreviousJob}>
                <i className="icon-left-open" />
                Previous Job
              </a>
              <p className="comp-sum-header__page-navigation-divider">|</p>
              <a className="comp-sum-header__page-navigation-link" onClick={goToNextJob}>
                Next Job
                <i className="icon-right-open" />
              </a>
            </div>
          )}
          <span className="comp-sum-modal__close" onClick={closeModal}>
            <i className="icon-cancel" />
          </span>
        </div>
      </div>
      <div className="comp-sum-header__row">
        <h2 className="pxl-header-xsmall comp-sum-header__title">{getPositionTitleHeader()}</h2>
      </div>
      <div className="comp-sum-header__row">
        <div className="pxl-text-xsmall comp-sum-header__audit-info">
          {dataAgedToDate && `All data aged to ${shortDateString(new Date(dataAgedToDate.replace(/-/g, '/')))}. `}
          {auditMessage}
        </div>
        <div className="comp-sum-header__actions">
          {!isConfigVisible ? (
            <React.Fragment>
              <div className="comp-sum-header__config-action" onClick={displaySettings} ref={pdfSettingsButtonRef}>
                <i className="icon-cog comp-sum-header__config-action-icon" />
                PDF Settings
              </div>
              <div className="comp-sum-header__config-action" onClick={toggleConfigModal}>
                <i className="icon-pencil comp-sum-header__config-action-icon" />
                Configure Report
              </div>
            </React.Fragment>
          ) : (
            <a className="comp-sum-header__config-action" onClick={toggleConfigModal}>
              Done
            </a>
          )}
          <Dropdown
            className="pxl-btn pxl-btn-primary pxl-btn-sm comp-sum-header__config-action-dropdown"
            dropdownClass="comp-sum-header__config-action-dropdown-items"
            itemOnClick={handleDropdownClick}
            dropdownItems={dropdownItems}
            icon={!isDownloading ? 'icon-download' : 'icon-spin icon-animate-spin'}
            text={!isDownloading ? 'Download' : 'Downloading'}
            id="csr_download-btn"
          />
        </div>
      </div>
      {showSettings && renderPopover()}
    </div>
  );
}

CompensationSummaryHeader.propTypes = {
  dataAgedToDate: string,
  isPageLoading: bool,
  lastModifiedDate: string,
  lastModifiedName: string,
  selectedJobIDs: arrayOf(oneOfType([number, string])),
  showModifedHeader: bool,
  useJobCodeKey: bool,
  pricingType: string,
  benchmarkYear: oneOfType([number, string]),
  downloadHandler: func,
  downloadToExcel: func,
  clearSelectedJobs: func,
  goToNextJob: func,
  goToPreviousJob: func,
  hideModal: func,
  saveConfigSettings: func,
  setSelectedBenchmarkYear: func,
  toggleConfig: func
};
export default CompensationSummaryHeader;
